// src/components/InventoryForm.tsx
import React, { useEffect, useRef, useState } from "react";
import { Inventory, ItemLocation, Visibility } from "../../types/Inventory";
import { isHtmlDocUrl, resolveInventoryUrl } from "../../api/InventoryAPI";
import { UPLOAD_MAX_FILE_MB_KEY, getSetting } from "../../api/SettingsAPI";
import { logApiError, getUserMessage } from "../../api/apiError";
import PhotosManager from "./PhotosManager";
import ServiceAreaEditor from "./ServiceAreaEditor";
import ItemLocationEditor from "./ItemLocationEditor";
import type { CoverageArea } from "../../utils/coverage";

type InventoryEditableField = "inventoryName" | "notes" | "description" | "url" | "visibility" | null;

export interface InventoryFormProps {
    inventory: Inventory | null;
    selectedCategoryName?: string;
    isNew: boolean;
    loading: boolean;
    onFieldChange: (field: keyof Inventory, value: any) => void;
    onSaveField?: (field: keyof Inventory, value: any) => Promise<void>;
    onSave: () => Promise<void>;
    onDelete?: () => Promise<void>;
    onUploadHtml?: (file: File) => Promise<void>;
    onRemoveHtml?: () => Promise<void>;
    onUploadPhoto?: (file: File) => Promise<void>;
    onDeletePhoto?: (photoId: string) => Promise<void>;
    onReorderPhotos?: (photoIdsInOrder: string[]) => Promise<void>;
    token?: string;
    onSetServiceArea?: (coverage: CoverageArea[]) => Promise<void>;
    categoryHasServiceArea?: boolean;
    onSetItemLocation?: (location: ItemLocation | null) => Promise<void>;
    categoryHasItemLocation?: boolean;
    /** Create mode: the parent renders one submit button at the bottom of
     *  the whole page (below area/location/parameters), so the form's own
     *  button is hidden. */
    hideCreateButton?: boolean;
}

const InventoryForm: React.FC<InventoryFormProps> = ({
    inventory,
    selectedCategoryName,
    isNew,
    loading,
    onFieldChange,
    onSaveField,
    onSave,
    onDelete,
    onUploadHtml,
    onRemoveHtml,
    onUploadPhoto,
    onDeletePhoto,
    onReorderPhotos,
    token,
    onSetServiceArea,
    categoryHasServiceArea,
    onSetItemLocation,
    categoryHasItemLocation,
    hideCreateButton,
}) => {
    const [editingField, setEditingField] = useState<InventoryEditableField>(null);
    const [draftValue, setDraftValue] = useState<any>("");
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [createError, setCreateError] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // ---- HTML document attachment state ----
    const [htmlFile, setHtmlFile] = useState<File | null>(null);
    const [htmlError, setHtmlError] = useState<string | null>(null);
    const [htmlBusy, setHtmlBusy] = useState(false);
    const htmlInputRef = useRef<HTMLInputElement>(null);

    // Listing "page" mode: an external link, or photos (an attached page).
    const [pageMode, setPageMode] = useState<"link" | "photos">("link");
    // Keep the mode in sync with the item: a listing with photos (or a legacy
    // attached page) opens in Photos mode, else External-link mode. Skipped on
    // the create→edit transition (id "" → real id): the just-created listing
    // has no photos yet, and syncing would clobber the mode chosen on the
    // create form.
    const prevIdRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        const id = inventory?.id ?? "";
        const justCreated = prevIdRef.current === "" && id !== "";
        prevIdRef.current = id;
        if (justCreated) return;
        const hasPhotos = (inventory?.photos?.length ?? 0) > 0;
        setPageMode(hasPhotos || isHtmlDocUrl(inventory?.url) ? "photos" : "link");
    }, [inventory?.id, inventory?.url, inventory?.photos?.length]);

    if (!inventory) return null;

    const hasHtmlDoc = isHtmlDocUrl(inventory.url);

    // Switching to an external link removes any attached photo page first (the
    // backend clears the url on removal), so we don't leave an orphaned page.
    const chooseLinkMode = async () => {
        if (hasHtmlDoc && onRemoveHtml) {
            const ok = window.confirm(
                "This listing has a photo page. Remove it and use an external link instead?"
            );
            if (!ok) return;
            setHtmlBusy(true);
            setHtmlError(null);
            try {
                await onRemoveHtml();
            } catch (err) {
                logApiError(err);
                setHtmlError(getUserMessage(err));
                return;
            } finally {
                setHtmlBusy(false);
            }
        }
        setPageMode("link");
    };

    const handleUploadHtml = async () => {
        if (!htmlFile || !onUploadHtml) return;
        setHtmlBusy(true);
        setHtmlError(null);
        try {
            await onUploadHtml(htmlFile);
            setHtmlFile(null);
            if (htmlInputRef.current) htmlInputRef.current.value = "";
        } catch (err) {
            logApiError(err);
            setHtmlError(getUserMessage(err));
        } finally {
            setHtmlBusy(false);
        }
    };

    const handleRemoveHtml = async () => {
        if (!onRemoveHtml) return;
        setHtmlBusy(true);
        setHtmlError(null);
        try {
            await onRemoveHtml();
        } catch (err) {
            logApiError(err);
            setHtmlError(getUserMessage(err));
        } finally {
            setHtmlBusy(false);
        }
    };

    // The live upload limit (upload_max_file_mb setting; absent = 10 MB),
    // shown to the user so rejections don't come as a surprise.
    const [uploadMb, setUploadMb] = useState("10");
    useEffect(() => {
        getSetting(UPLOAD_MAX_FILE_MB_KEY)
            .then((v) => { if (v && Number(v) > 0) setUploadMb(v); })
            .catch(() => {}); // hint only — keep the default text
    }, []);

    // Photos section (rendered in edit mode when the Photos page-mode is chosen).
    const htmlDocSection = (onUploadPhoto && inventory.id) ? (
        <div className="bh-row">
            <label className="bh-label">Photos</label>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                <small style={{ color: "var(--text-secondary)" }}>
                    Add as many photos as you like, one at a time — each up to {uploadMb} MB
                    (image files only, e.g. JPEG or PNG).
                </small>
                <PhotosManager
                    inventoryId={inventory.id}
                    photos={inventory.photos ?? []}
                    onUpload={onUploadPhoto}
                    onDelete={onDeletePhoto ?? (async () => {})}
                    onReorder={onReorderPhotos ?? (async () => {})}
                    disabled={loading}
                />

                {/* Legacy: a previously-attached self-contained HTML page. */}
                {(hasHtmlDoc || onUploadHtml) && (
                    <details className="bh-details">
                        <summary>Advanced: attached HTML page (click to open)</summary>
                        <small style={{ display: "block", color: "var(--text-secondary)", marginTop: "0.35rem" }}>
                            Optional: attach a single self-contained HTML page (up to {uploadMb} MB)
                            as the listing page. It takes the place of an external link — your
                            photos above are kept either way.
                        </small>
                        {hasHtmlDoc && (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.35rem" }}>
                                <a href={resolveInventoryUrl(inventory.url)} target="_blank" rel="noopener noreferrer">
                                    Open attached page ↗
                                </a>
                                {onRemoveHtml && (
                                    <button type="button" className="btn-sm" onClick={handleRemoveHtml} disabled={loading || htmlBusy}>
                                        Remove
                                    </button>
                                )}
                            </div>
                        )}
                        {onUploadHtml && (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.35rem" }}>
                                <input
                                    ref={htmlInputRef}
                                    type="file"
                                    accept=".html,.htm,text/html"
                                    onChange={(e) => setHtmlFile(e.target.files?.[0] ?? null)}
                                    disabled={loading || htmlBusy}
                                />
                                <button type="button" className="btn-sm" onClick={handleUploadHtml} disabled={loading || htmlBusy || !htmlFile}>
                                    {hasHtmlDoc ? "Replace" : "Upload"}
                                </button>
                            </div>
                        )}
                    </details>
                )}

                {htmlError && (
                    <div className="error-message" style={{ marginTop: "0.25rem" }}>{htmlError}</div>
                )}
            </div>
        </div>
    ) : null;

    const startEdit = (field: InventoryEditableField, value: any) => {
        setEditingField(field);
        setDraftValue(value);
        setFieldError(null);
    };

    const cancelEdit = () => {
        setEditingField(null);
        setDraftValue("");
        setFieldError(null);
    };

    const saveEdit = async () => {
        if (!editingField || !onSaveField) return;
        try {
            await onSaveField(editingField, draftValue);
            setEditingField(null);
            setDraftValue("");
            setFieldError(null);
        } catch (err) {
            logApiError(err);
            setFieldError(getUserMessage(err));
        }
    };

    // CREATE MODE: all fields always editable, single save button
    if (isNew) {
        return (
            <div className="inventory-form">
                <div className="bh-row">
                    <div className="bh-label">Selected Category</div>
                    <div className="bh-field">{selectedCategoryName || "(none selected)"}</div>
                </div>

                <div className="bh-row">
                    <label className="bh-label">Name</label>
                    <input
                        className="bh-input"
                        type="text"
                        value={inventory.inventoryName}
                        onChange={(e) => onFieldChange("inventoryName", e.target.value)}
                        disabled={loading}
                    />
                </div>

                <div className="bh-row">
                    <label className="bh-label">Notes (private)</label>
                    <input
                        className="bh-input"
                        type="text"
                        value={inventory.notes}
                        onChange={(e) => onFieldChange("notes", e.target.value)}
                        disabled={loading}
                        placeholder="Only you (the store) see this"
                    />
                </div>

                <div className="bh-row">
                    <label className="bh-label">Description</label>
                    <textarea
                        className="bh-input"
                        rows={4}
                        value={inventory.description}
                        onChange={(e) => onFieldChange("description", e.target.value)}
                        disabled={loading}
                        placeholder="Public — shown to searchers (line breaks are kept)"
                    />
                </div>

                {/* Listing page: an external link OR photos (added after create) */}
                <div className="bh-row">
                    <label className="bh-label">Listing page</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", alignItems: "flex-start" }}>
                        <label style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", cursor: "pointer", fontWeight: 400 }}>
                            <input
                                type="radio"
                                name="pageModeCreate"
                                checked={pageMode === "link"}
                                onChange={() => setPageMode("link")}
                                disabled={loading}
                            />
                            External link
                        </label>
                        <label style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", cursor: "pointer", fontWeight: 400 }}>
                            <input
                                type="radio"
                                name="pageModeCreate"
                                checked={pageMode === "photos"}
                                onChange={() => {
                                    setPageMode("photos");
                                    onFieldChange("url", "");
                                }}
                                disabled={loading}
                            />
                            Photos
                        </label>
                    </div>
                </div>

                {pageMode === "link" ? (
                    <div className="bh-row">
                        <label className="bh-label">URL</label>
                        <input
                            className="bh-input"
                            type="text"
                            value={inventory.url}
                            onChange={(e) => onFieldChange("url", e.target.value)}
                            disabled={loading}
                            placeholder="Link to a page about this item"
                        />
                    </div>
                ) : (
                    <div className="bh-row">
                        <div className="bh-label" />
                        <small style={{ color: "var(--text-secondary)" }}>
                            The photo tools appear right after you create the listing.
                        </small>
                    </div>
                )}

                <div className="bh-row">
                    <label className="bh-label">Visibility</label>
                    <select
                        className="bh-input"
                        value={inventory.visibility}
                        onChange={(e) => onFieldChange("visibility", e.target.value as Visibility)}
                        disabled={loading}
                    >
                        <option value={Visibility.PUBLIC}>PUBLIC</option>
                        <option value={Visibility.PRIVATE}>PRIVATE</option>
                    </select>
                </div>

                {!hideCreateButton && (
                    <div className="bh-row buttons-row">
                        <button
                            onClick={async () => {
                                setCreateError(null);
                                try {
                                    await onSave();
                                } catch (err) {
                                    logApiError(err);
                                    setCreateError(getUserMessage(err));
                                }
                            }}
                            disabled={loading}
                        >
                            Create Inventory
                        </button>
                        {createError && (
                            <div className="error-message" style={{ marginTop: "0.5rem" }}>{createError}</div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // EDIT MODE: per-field edit/draft/save/cancel pattern (same as CategoryForm)
    return (
        <div className="inventory-form">
            <div className="bh-row">
                <div className="bh-label">Selected Category</div>
                <div className="bh-field">{selectedCategoryName || "(none selected)"}</div>
            </div>

            {/* Name */}
            <div className="bh-row">
                <label className="bh-label">Name</label>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <input
                        className="bh-input"
                        type="text"
                        value={editingField === "inventoryName" ? draftValue : inventory.inventoryName}
                        onChange={(e) => setDraftValue(e.target.value)}
                        disabled={loading || editingField !== "inventoryName"}
                    />
                    {editingField === "inventoryName" ? (
                        <>
                            <button type="button" className="btn-sm" onClick={saveEdit} disabled={loading}>Save</button>
                            <button type="button" className="btn-sm" onClick={cancelEdit} disabled={loading}>Cancel</button>
                        </>
                    ) : (
                        <button
                            type="button"
                            className="btn-sm"
                            onClick={() => startEdit("inventoryName", inventory.inventoryName)}
                            disabled={loading || editingField !== null}
                        >
                            Edit
                        </button>
                    )}
                </div>
                {editingField === "inventoryName" && fieldError && (
                    <div className="error-message" style={{ marginTop: "0.35rem" }}>{fieldError}</div>
                )}
            </div>

            {/* Notes (private, owner-only) */}
            <div className="bh-row">
                <label className="bh-label">Notes (private)</label>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <input
                        className="bh-input"
                        type="text"
                        value={editingField === "notes" ? draftValue : inventory.notes}
                        onChange={(e) => setDraftValue(e.target.value)}
                        disabled={loading || editingField !== "notes"}
                    />
                    {editingField === "notes" ? (
                        <>
                            <button type="button" className="btn-sm" onClick={saveEdit} disabled={loading}>Save</button>
                            <button type="button" className="btn-sm" onClick={cancelEdit} disabled={loading}>Cancel</button>
                        </>
                    ) : (
                        <button
                            type="button"
                            className="btn-sm"
                            onClick={() => startEdit("notes", inventory.notes)}
                            disabled={loading || editingField !== null}
                        >
                            Edit
                        </button>
                    )}
                </div>
                {editingField === "notes" && fieldError && (
                    <div className="error-message" style={{ marginTop: "0.35rem" }}>{fieldError}</div>
                )}
            </div>

            {/* Description (public, shown to searchers) */}
            <div className="bh-row">
                <label className="bh-label">Description</label>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                    <textarea
                        className="bh-input"
                        rows={4}
                        value={editingField === "description" ? draftValue : inventory.description}
                        onChange={(e) => setDraftValue(e.target.value)}
                        disabled={loading || editingField !== "description"}
                    />
                    {editingField === "description" ? (
                        <>
                            <button type="button" className="btn-sm" onClick={saveEdit} disabled={loading}>Save</button>
                            <button type="button" className="btn-sm" onClick={cancelEdit} disabled={loading}>Cancel</button>
                        </>
                    ) : (
                        <button
                            type="button"
                            className="btn-sm"
                            onClick={() => startEdit("description", inventory.description)}
                            disabled={loading || editingField !== null}
                        >
                            Edit
                        </button>
                    )}
                </div>
                {editingField === "description" && fieldError && (
                    <div className="error-message" style={{ marginTop: "0.35rem" }}>{fieldError}</div>
                )}
            </div>

            {/* Listing page: an external link OR photos (an attached page) */}
            <div className="bh-row">
                <label className="bh-label">Listing page</label>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", alignItems: "flex-start" }}>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", cursor: "pointer", fontWeight: 400 }}>
                        <input
                            type="radio"
                            name="pageMode"
                            checked={pageMode === "link"}
                            onChange={chooseLinkMode}
                            disabled={loading || htmlBusy}
                        />
                        External link
                    </label>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", cursor: onUploadPhoto ? "pointer" : "default", fontWeight: 400 }}>
                        <input
                            type="radio"
                            name="pageMode"
                            checked={pageMode === "photos"}
                            onChange={() => setPageMode("photos")}
                            disabled={loading || htmlBusy || !onUploadPhoto}
                        />
                        Photos
                    </label>
                </div>
            </div>

            {/* URL (external link mode) */}
            {pageMode === "link" && (
                <div className="bh-row">
                    <label className="bh-label">URL</label>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                        <input
                            className="bh-input"
                            type="text"
                            value={editingField === "url" ? draftValue : inventory.url}
                            onChange={(e) => setDraftValue(e.target.value)}
                            disabled={loading || editingField !== "url"}
                        />
                        {editingField === "url" ? (
                            <>
                                <button type="button" className="btn-sm" onClick={saveEdit} disabled={loading}>Save</button>
                                <button type="button" className="btn-sm" onClick={cancelEdit} disabled={loading}>Cancel</button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="btn-sm"
                                onClick={() => startEdit("url", inventory.url)}
                                disabled={loading || editingField !== null}
                            >
                                Edit
                            </button>
                        )}
                    </div>
                    {editingField === "url" && fieldError && (
                        <div className="error-message" style={{ marginTop: "0.35rem" }}>{fieldError}</div>
                    )}
                </div>
            )}

            {/* Photos (attached page mode) */}
            {pageMode === "photos" && htmlDocSection}

            {/* Service area — only for service categories */}
            {onSetServiceArea && inventory.id && categoryHasServiceArea && (
                <div className="bh-row bh-row--separated">
                    <label className="bh-label">Service area</label>
                    <details className="bh-details" open>
                        <summary>Where you provide the service (click to open/close)</summary>
                        <div style={{ marginTop: "0.5rem" }}>
                            <ServiceAreaEditor
                                token={token ?? ""}
                                coverage={inventory.coverage ?? []}
                                onSave={onSetServiceArea}
                                disabled={loading}
                            />
                        </div>
                    </details>
                </div>
            )}

            {/* Item location — only for fixed-location categories (rentals, real estate) */}
            {onSetItemLocation && inventory.id && categoryHasItemLocation && (
                <div className="bh-row bh-row--separated">
                    <label className="bh-label">Location</label>
                    <details className="bh-details" open>
                        <summary>Where this is located (click to open/close)</summary>
                        <div style={{ marginTop: "0.5rem" }}>
                            <ItemLocationEditor
                                token={token ?? ""}
                                location={inventory.location ?? null}
                                onSave={onSetItemLocation}
                                disabled={loading}
                            />
                        </div>
                    </details>
                </div>
            )}

            {/* Visibility */}
            <div className="bh-row">
                <label className="bh-label">Visibility</label>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <select
                        className="bh-input"
                        value={editingField === "visibility" ? draftValue : inventory.visibility}
                        onChange={(e) => setDraftValue(e.target.value as Visibility)}
                        disabled={loading || editingField !== "visibility"}
                    >
                        <option value={Visibility.PUBLIC}>PUBLIC</option>
                        <option value={Visibility.PRIVATE}>PRIVATE</option>
                    </select>
                    {editingField === "visibility" ? (
                        <>
                            <button type="button" className="btn-sm" onClick={saveEdit} disabled={loading}>Save</button>
                            <button type="button" className="btn-sm" onClick={cancelEdit} disabled={loading}>Cancel</button>
                        </>
                    ) : (
                        <button
                            type="button"
                            className="btn-sm"
                            onClick={() => startEdit("visibility", inventory.visibility)}
                            disabled={loading || editingField !== null}
                        >
                            Edit
                        </button>
                    )}
                </div>
                {editingField === "visibility" && fieldError && (
                    <div className="error-message" style={{ marginTop: "0.35rem" }}>{fieldError}</div>
                )}
            </div>

            {onDelete && (
                <div className="bh-row buttons-row">
                    <button
                        type="button"
                        onClick={async () => {
                            setDeleteError(null);
                            try {
                                await onDelete();
                            } catch (err) {
                                logApiError(err);
                                setDeleteError(getUserMessage(err));
                            }
                        }}
                        disabled={loading}
                        className="danger"
                    >
                        Delete Inventory
                    </button>
                    {deleteError && (
                        <div className="error-message" style={{ marginTop: "0.35rem" }}>{deleteError}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default InventoryForm;
